// Wave / threat model (era-gated). Combat is fully automatic: a wave has a
// single "threat" number, the settlement has a single "defense" number, and the
// larger one wins. Threat is driven by the settlement's AGE, not the raw wave
// index: within an age raider waves ramp gently, and the era BOSS is the age
// gate. Everything here derives those numbers so the curve is tunable in one
// place (plus BALANCE.waves).

import { BALANCE } from './balance';
import { visualAge } from './visual-ages';
import { getLang } from '@i18n/i18n';

/** Threat of a raider wave in a given age. Within an age, each wave survived
 *  (wavesThisAge) adds a gentle ramp, so camping an age grinds you down; the
 *  band steps up hard between ages (ageThreatGrowth^age). */
export function raiderThreat(age: number, wavesThisAge: number): number {
  const w = BALANCE.waves;
  const band = w.baseThreat * Math.pow(w.ageThreatGrowth, Math.max(0, age));
  const ramp = 1 + Math.max(0, wavesThisAge) * w.withinAgeRamp;
  return band * ramp;
}

/** The era-boss multiplier that gates a given age. Escalates per age gate,
 *  capped, so later gates are real achievements without being unbounded. The
 *  FIRST gate is a flat, gentler number: it is the first boss the player has
 *  ever seen, and it was ending most runs before the loop had taught anything. */
export function gateBossMult(age: number): number {
  const w = BALANCE.waves;
  if (Math.max(0, age) <= BALANCE.ages.firstGateAge) return w.firstGateBossMult;
  return Math.min(w.gateBossMult + Math.max(0, age) * w.gateBossRamp, w.gateBossMax);
}

/** Threat of the era boss standing at the gate of a given age: a dramatic spike
 *  over that age's raider band. Beating it is the only way to advance the age. */
export function gateBossThreat(age: number): number {
  const w = BALANCE.waves;
  const band = w.baseThreat * Math.pow(w.ageThreatGrowth, Math.max(0, age));
  return band * gateBossMult(age);
}

/** Run-currency (spoils) granted for clearing a wave in a given age. Follows
 *  the threat model: scales with the AGE band, flat within an age (camping a
 *  gate earns linear spoils against a climbing threat ramp). The era boss pays
 *  double, on top of its artifact drop. */
export function waveSpoils(age: number, isBoss: boolean): number {
  const w = BALANCE.waves;
  let spoils = w.baseSpoils * Math.pow(w.spoilsAgeGrowth, Math.max(0, age));
  if (isBoss) spoils *= 2;
  return Math.ceil(spoils);
}

export type EnemyKind = 'grunt' | 'brute' | 'boss';

/** Enemy composition for a wave, chosen per spawned unit. Kind scales with the
 *  settlement's ERA, NOT the raw wave index: early eras field foot raiders
 *  (grunts), later eras field war machines (brutes), so the on-screen menace
 *  matches the age-based threat model (a low age never faces a full machine
 *  army just because the wave count is high). On a boss wave the first unit is
 *  the boss and the rest are its machine escort. */
export function enemyKindAt(
  era: number,
  index: number,
  count: number,
  isBoss: boolean,
): EnemyKind {
  if (isBoss) return index === 0 ? 'boss' : 'brute';
  // era 0 -> all foot raiders; ramps to a mostly-machine army by era 4.
  const bruteFraction = Math.max(0, Math.min(era * 0.25, 0.85));
  return count > 0 && index / count < bruteFraction ? 'brute' : 'grunt';
}

// Flavor labels for the incoming-wave banner, localized per active language
// (these are shown on screen, so a Turkish-only list leaked into every locale).
// Turkish is the source; en/de/es/fr drafted by Kimi K3 and curated.
const RAIDER_NAMES: Record<string, string[]> = {
  tr: ['Yağmacılar', 'Göçebe Akını', 'Çapulcular', 'Atlı Akıncılar', 'Düşman Boy', 'Bozok Çetesi', 'Karanlık Boy', 'Bozkır Kurtları'],
  en: ['Plunderers', 'Nomad Raid', 'Marauders', 'Mounted Raiders', 'Enemy Tribe', 'Bozok Warband', 'Dark Tribe', 'Steppe Wolves'],
  de: ['Plünderer', 'Nomadenüberfall', 'Marodeure', 'Reiterräuber', 'Feindlicher Stamm', 'Bozok-Bande', 'Finsterer Stamm', 'Steppenwölfe'],
  es: ['Saqueadores', 'Incursión Nómada', 'Merodeadores', 'Incursores a Caballo', 'Tribu Enemiga', 'Banda Bozok', 'Tribu Oscura', 'Lobos Esteparios'],
  fr: ['Pillards', 'Razzia Nomade', 'Maraudeurs', 'Razzieurs à Cheval', 'Tribu Ennemie', 'Bande Bozok', 'Tribu Sombre', 'Loups des Steppes'],
};
// Names follow the exact historical/fictional age, just like the rendered army.
const BOSS_NAMES: Record<string, string[]> = {
  tr: ['İstilacı Han','Kara Kağan','Kuşatma Beyi','Büyük Bombard','Zırhlı Tümen','Yörünge Kruvazörü','Gezegen Kuşatıcısı','Yıldız Dretnotu','Galaksi Filosu','Hiçlik Muhafızı','Gök Yargıcı','Ebedi Nöbetçi'],
  en: ['Invader Khan','Black Khagan','Siege Lord','Great Bombard','Armored Division','Orbital Cruiser','Planetary Besieger','Stellar Dreadnought','Galactic Fleet','Void Warden','Sky Arbiter','Eternal Sentinel'],
};
const FUTURE_RAIDERS: Record<string, string[]> = {
  tr: ['Zırhlı Birlikler','Yörünge Akıncıları','Gezegen Birlikleri','Yıldız Filosu','Galaksi Akıncıları','Hiçlik Filosu','Enerji Muhafızları','Ebedi Lejyon'],
  en: ['Armored Forces','Orbital Raiders','Planetary Forces','Stellar Fleet','Galactic Raiders','Void Fleet','Energy Wardens','Eternal Legion'],
};

/** The name list for the active language (English fallback). */
function localized(lists: Record<string, string[]>): string[] {
  return lists[getLang()] ?? lists.en;
}

/** Raider band name for a raw wave index, in the active language. */
export function waveName(wave: number, age = 0): string {
  if (age >= 4) return localized(FUTURE_RAIDERS)[visualAge(age) - 4];
  const list = localized(RAIDER_NAMES);
  return list[Math.max(0, wave - 1) % list.length];
}

/** The era boss guarding the gate of a given age, in the active language. */
export function gateBossName(age: number): string {
  const list = localized(BOSS_NAMES);
  return list[visualAge(age)];
}
